import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { initLog, log } from 'evlog/client';
import {
  getIdToken,
  parseActionCodeURL,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, redirect } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { toast } from 'sonner';
import * as Video from 'twilio-video';

import {
  APIError,
  type JoinMeetingAsGuest,
  Unauthorized,
} from '@virtual-care/client';
import { client as newClient } from '@virtual-care/hooks';
import { Toaster, TooltipProvider } from '@virtual-care/shadcn';
import {
  type MeetingLoaderData,
  errorToFormIssue,
  initializeTwilio,
  client as legacyClient,
  mapMessage,
} from '@virtual-care/ui';

import { requiresAuth } from './app/auth-loader.ts';
import { auth, getIdentity } from './app/firebase';
import Layout from './app/layout.tsx';
import AppointmentReview from './app/routes/appointment-review.tsx';
import Appointment from './app/routes/appointment/appointment.tsx';
import Appointments from './app/routes/appointments.tsx';
import B2BForgotPassword from './app/routes/b2b-forgot-password.tsx';
import B2BLogin from './app/routes/b2b-login.tsx';
import B2BResetPassword, {
  B2BResetPasswordError,
} from './app/routes/b2b-reset-password.tsx';
import AppointmentReviewSubmitted from './app/routes/booking/appointment-review-submitted.tsx';
import AppointmentTime, {
  appointmentTimeLoader,
} from './app/routes/booking/appointment-time.tsx';
import BookAppointment from './app/routes/booking/book-appointment.tsx';
import BookingConfirmation, {
  bookingConfirmationLoader,
} from './app/routes/booking/booking-confirmation.tsx';
import BookingLayout from './app/routes/booking/booking-layout.tsx';
import BookingPayments, {
  bookingPaymentsLoader,
} from './app/routes/booking/payments.tsx';
import RequestStatus, {
  bookingRequestStatusLoader,
} from './app/routes/booking/request-status.tsx';
import SymptomChecker, {
  symptomCheckerLoader,
} from './app/routes/booking/symptom-checker.tsx';
import ChangePassword from './app/routes/change-password.tsx';
import CompleteProfile from './app/routes/complete-profile.tsx';
import GuestMeeting from './app/routes/guest-meeting.tsx';
import Home from './app/routes/home/home.tsx';
import HomecareBookService, {
  homecareBookServiceLoader,
} from './app/routes/homecare/book-service.tsx';
import HomecareBookingConfirmation, {
  homecareBookingConfirmationLoader,
} from './app/routes/homecare/booking-confirmation.tsx';
import HomecareLayout from './app/routes/homecare/homecare-layout.tsx';
import HomecarePayments, {
  homecarePaymentsLoader,
} from './app/routes/homecare/payments.tsx';
import HomecareRequestStatus, {
  homecareRequestStatusLoader,
} from './app/routes/homecare/request-status.tsx';
import HomecareReservation from './app/routes/homecare/reservation/homecare-reservation.tsx';
import HomecareServiceListing from './app/routes/homecare/service-listing.tsx';
import JoinEnded from './app/routes/join-ended.tsx';
import JoinError from './app/routes/join-error.tsx';
import Logout from './app/routes/logout.tsx';
import MedicalRecords from './app/routes/medical-records.tsx';
import Meeting from './app/routes/meeting.tsx';
import Notifications from './app/routes/notifications.tsx';
import PrintingRequest from './app/routes/printing/printing-request.tsx';
import Printing from './app/routes/printing/printing.tsx';
import Profile from './app/routes/profile.tsx';
import BookSecondOpinionAppointment from './app/routes/second-opinion/book-appointment.tsx';
import SecondOpinionBookingConfirmation, {
  secondOpinionBookingConfirmationLoader,
} from './app/routes/second-opinion/booking-confirmation.tsx';
import SecondOpinionPayments, {
  secondOpinionPaymentsLoader,
} from './app/routes/second-opinion/payments.tsx';
import SecondOpinionRequestStatus, {
  secondOpinionRequestStatusLoader,
} from './app/routes/second-opinion/request-status.tsx';
import SecondOpinionLayout from './app/routes/second-opinion/second-opinion-layout.tsx';
import SecondOpinion from './app/routes/second-opinion/second-opinion.tsx';
import SecondOpinionSymptomChecker, {
  secondOpinionSymptomCheckerLoader,
} from './app/routes/second-opinion/symptom-checker.tsx';
import SignIn from './app/routes/signin';
import Register from './app/routes/signup.tsx';
import Specialties from './app/routes/specialties.tsx';
import PhoneVerification from './app/routes/verify-phone';
import i18n from './i18n';

initLog({
  service: 'patients',
  transport: {
    // Ship logs from production builds only; local dev stays console-only.
    enabled: import.meta.env.PROD,
    endpoint: `${import.meta.env.VITE_API_URL}/evlog/ingest`,
  },
});

const router = createBrowserRouter([
  {
    path: '/',
    Component: Layout,
    loader: requiresAuth,
    children: [
      {
        loader: requiresAuth,
        index: true,
        Component: Home,
      },
      {
        path: 'book-appointment',
        Component: BookingLayout,
        children: [
          {
            index: true,
            Component: BookAppointment,
          },
          {
            path: ':specialtyId/symptom-checker',
            Component: SymptomChecker,
            loader: symptomCheckerLoader,
          },
          {
            path: ':specialtyId/appointment-time',
            Component: AppointmentTime,
            loader: appointmentTimeLoader,
          },
          {
            path: 'booking-confirmation',
            Component: BookingConfirmation,
            loader: bookingConfirmationLoader,
          },
          {
            path: 'request-status',
            Component: RequestStatus,
            loader: bookingRequestStatusLoader,
          },
        ],
      },
      {
        path: 'second-opinion',
        Component: SecondOpinionLayout,
        children: [
          {
            index: true,
            Component: BookSecondOpinionAppointment,
          },
          {
            path: ':id',
            Component: SecondOpinion,
          },
          {
            path: ':specialtyId/symptom-checker',
            Component: SecondOpinionSymptomChecker,
            loader: secondOpinionSymptomCheckerLoader,
          },
          {
            path: 'booking-confirmation',
            Component: SecondOpinionBookingConfirmation,
            loader: secondOpinionBookingConfirmationLoader,
          },
          {
            path: 'request-status',
            Component: SecondOpinionRequestStatus,
            loader: secondOpinionRequestStatusLoader,
          },
        ],
      },
      {
        path: 'printing',
        children: [
          {
            index: true,
            Component: Printing,
          },
          {
            path: ':id',
            Component: PrintingRequest,
          },
        ],
      },
      {
        path: 'homecare',
        Component: HomecareLayout,
        children: [
          {
            index: true,
            Component: HomecareServiceListing,
          },
          {
            path: 'reservations/:id',
            Component: HomecareReservation,
          },
          {
            path: 'vendor/:vendorId/book',
            Component: HomecareBookService,
            loader: homecareBookServiceLoader,
          },
          {
            path: 'booking-confirmation',
            Component: HomecareBookingConfirmation,
            loader: homecareBookingConfirmationLoader,
          },
          {
            path: 'request-status',
            Component: HomecareRequestStatus,
            loader: homecareRequestStatusLoader,
          },
        ],
      },
      {
        path: 'specialties',
        Component: Specialties,
      },
      {
        path: 'appointments',
        children: [
          {
            index: true,
            Component: Appointments,
          },
          {
            path: ':id',
            Component: Appointment,
          },
        ],
      },
      {
        path: 'results',
        Component: MedicalRecords,
      },
      {
        path: 'profile',
        Component: Profile,
      },
      {
        path: 'notifications',
        Component: Notifications,
      },
      {
        path: 'logout',
        Component: Logout,
      },
      {
        path: '/review/:meetingId',
        Component: AppointmentReviewSubmitted,
        loader: requiresAuth,
      },
    ],
  },
  {
    Component: BookingLayout,
    children: [
      {
        path: 'book-appointment/payments',
        loader: bookingPaymentsLoader,
        Component: BookingPayments,
      },
    ],
  },
  {
    Component: SecondOpinionLayout,
    children: [
      {
        path: 'second-opinion/payments',
        loader: secondOpinionPaymentsLoader,
        Component: SecondOpinionPayments,
      },
    ],
  },
  {
    Component: HomecareLayout,
    children: [
      {
        path: 'homecare/payments',
        loader: homecarePaymentsLoader,
        Component: HomecarePayments,
      },
    ],
  },
  {
    path: '/appointment-review/:reservationId/:meetingId',
    Component: AppointmentReview,
    loader: requiresAuth,
  },
  {
    path: '/meeting/:reservationId',
    Component: Meeting,
    loader: async ({ params }) => {
      const identity = await requiresAuth();

      const { reservationId } = params;
      if (!reservationId) {
        throw redirect('/');
      }
      const joinToken = await newClient.request(
        'POST /meetings/reservations/{reservationId}/join',
        { reservationId },
      );
      const reservation = await newClient.request('GET /reservations/{id}', {
        id: reservationId,
      });

      const twilioClient = await initializeTwilio(joinToken);
      const conversation = await twilioClient.getConversationByUniqueName(
        joinToken.conversationId,
      );

      const messagePaginator = await conversation.getMessages(100);
      return {
        joinToken,
        reservation,
        conversation,
        messages: messagePaginator.items.map((it) =>
          mapMessage(it, identity.id),
        ),
        uid: identity.id,
        room: await Video.connect(joinToken.token, {
          name: joinToken.roomName,
          audio: true,
          video: { width: 640, height: 480 },
          dominantSpeaker: true,
        }),
      } satisfies MeetingLoaderData;
    },
  },
  {
    path: '/register',
    Component: Register,
    loader: async () => {
      const identity = await getIdentity();
      if (!identity) {
        return null;
      }
      throw redirect('/');
    },
  },
  {
    path: '/complete',
    Component: CompleteProfile,
    loader: async () => {
      const identity = await getIdentity();
      if (!identity) {
        throw redirect('/signin');
      }
      // if the user is B2B, redirect to main screen
      if (identity.userType === 'B2B') {
        throw redirect('/');
      }
      // if the user has a phone number but no B2C profile, allow to complete the profile
      if (identity.phoneNumber && !identity.b2cProfile) {
        return null;
      }

      throw redirect('/');
    },
  },
  {
    path: '/signin',
    Component: SignIn,
    loader: async () => {
      const identity = await getIdentity();
      if (!identity) {
        return null;
      }
      throw redirect('/');
    },
  },
  {
    path: '/b2b-login',
    Component: B2BLogin,
    loader: async () => {
      const identity = await getIdentity();
      if (!identity) {
        return null;
      }
      throw redirect('/');
    },
  },
  {
    path: '/b2b-forgot-password',
    Component: B2BForgotPassword,
    loader: async () => {
      const identity = await getIdentity();
      if (!identity) {
        return null;
      }
      throw redirect('/');
    },
  },
  {
    path: '/change-password',
    Component: ChangePassword,
    loader: async () => {
      const identity = await getIdentity();
      if (!identity) {
        throw redirect('/signin');
      }
      return identity;
    },
  },
  {
    path: '/b2b-reset-password',
    Component: B2BResetPassword,
    ErrorBoundary: B2BResetPasswordError,
    loader: async ({ request }) => {
      const actionCodeURL = parseActionCodeURL(request.url);
      if (!actionCodeURL) {
        throw new Error('auth.b2bResetPassword.errors.linkNotFound');
      }
      if (actionCodeURL.operation !== 'PASSWORD_RESET') {
        throw new Error('auth.b2bResetPassword.errors.linkNotFound');
      }
      if (!actionCodeURL.code) {
        throw new Error('auth.b2bResetPassword.errors.codeMissing');
      }

      try {
        const email = await verifyPasswordResetCode(auth, actionCodeURL.code);
        return { email, oobCode: actionCodeURL.code };
      } catch (error) {
        log.error({ action: 'verify_reset_code', error });
        throw new Error('auth.b2bResetPassword.errors.invalidOrExpired');
      }
    },
  },
  {
    path: '/verify-phone',
    Component: PhoneVerification,
    loader: () => {
      if (sessionStorage.getItem('verificationId')) {
        return null;
      }
      throw redirect('/signin');
    },
  },
  {
    path: '/join/:token',
    Component: GuestMeeting,
    ErrorBoundary: JoinError,
    loader: async ({ params }) => {
      const { token } = params;
      if (!token) {
        throw redirect('/');
      }

      // TODO: the generated dispatch type for `POST /meetings/guest/join`
      // strips fields present in the documented `JoinMeetingAsGuest` output
      // (id, createdAt, endedAt, reservationId, roomName, ...). Fix the
      // OpenAPI metadata in apps/backend/src/routes/meeting.route.ts so this
      // cast is no longer needed.
      const response = (await newClient.request('POST /meetings/guest/join', {
        token,
      })) as JoinMeetingAsGuest;
      const { reservation, ...joinToken } = response;

      const twilioClient = await initializeTwilio(joinToken);
      const conversation = await twilioClient.getConversationByUniqueName(
        joinToken.conversationId,
      );
      const messagePaginator = await conversation.getMessages(100);

      return {
        joinToken,
        reservation,
        conversation,
        messages: messagePaginator.items.map((it) =>
          mapMessage(it, reservation.patientId),
        ),
        uid: reservation.patientId,
        room: await Video.connect(joinToken.token, {
          name: joinToken.roomName,
          audio: true,
          video: { width: 640, height: 480 },
          dominantSpeaker: true,
        }),
      } satisfies MeetingLoaderData;
    },
  },
  {
    path: '/join/ended',
    Component: JoinEnded,
  },
]);

function isFeatureDisabledError(error: unknown): boolean {
  if (!(error instanceof APIError) || error.status !== 403) {
    return false;
  }
  const data = error.data;
  if (typeof data !== 'object' || data === null || !('cause' in data)) {
    return false;
  }
  const cause = data.cause;
  if (typeof cause !== 'object' || cause === null || !('code' in cause)) {
    return false;
  }
  return cause.code === 'settings/feature-disabled';
}

function invalidateSettingsCache() {
  queryClient.invalidateQueries({
    predicate: (query) => query.meta?.endpoint === 'GET /settings/public',
  });
}

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error) => {
      log.error({ action: 'mutation_error', error });
      if (isFeatureDisabledError(error)) {
        toast.error(i18n.t('shared.api.featureDisabled'));
        invalidateSettingsCache();
        return;
      }
      if (error instanceof Unauthorized) {
        if (error.status === 401) {
          toast.error(i18n.t('shared.api.unauthorized'));
          window.location.href = '/';
        }
      } else if (import.meta.env.DEV) {
        const formIssue = errorToFormIssue(error);
        for (const propName in formIssue) {
          toast.error(
            `Error in propName "${propName}": ${formIssue[propName].map((issue) => issue.message).join(', ')}`,
          );
        }
      }
    },
  }),
  queryCache: new QueryCache({
    onError: (error) => {
      log.error({ action: 'query_error', error });
      if (isFeatureDisabledError(error)) {
        invalidateSettingsCache();
        return;
      }
      const formIssue = errorToFormIssue(error);
      for (const propName in formIssue) {
        if (import.meta.env.DEV) {
          toast.error(
            `Error in propName "${propName}": ${formIssue[propName].map((issue) => issue.message).join(', ')}`,
          );
        }
      }
    },
  }),
});

// Set the token for API client
legacyClient.setOptions({
  baseUrl: import.meta.env.VITE_API_URL,
  token: () => {
    if (auth.currentUser) {
      return getIdToken(auth.currentUser);
    }
    return '';
  },
});
newClient.setOptions({
  baseUrl: import.meta.env.VITE_API_URL,
  token: () => {
    if (auth.currentUser) {
      return getIdToken(auth.currentUser);
    }
    return '';
  },
});

createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <Toaster />
    <TooltipProvider>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </TooltipProvider>
  </StrictMode>,
);
